// AI Newsletter Generator — wizard → editor → preview → send.
// Extends the existing Newsletter admin section. Reuses the API client
// (../../lib/api.js) X-Admin-Password auth flow.
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Wand2, Send, RefreshCw, Save, Eye, X, Play, Clock, Loader2, ChevronRight } from "lucide-react";
import { apiClient } from "../../lib/api";

const REWRITE_LABELS = {
  rewrite: "Rewrite", shorter: "Shorter", longer: "Longer",
  professional: "More professional", exciting: "More exciting",
  urgency: "Add urgency", conversion: "Improve conversion",
  grammar: "Fix grammar", variation: "New variation",
  add_emojis: "Add emojis", remove_emojis: "Remove emojis",
};

const LABEL = (k) =>
  (k || "")
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

async function api(path, opts = {}, pw) {
  const r = await apiClient({
    url: `/admin/ai-newsletter${path}`,
    method: opts.method || "GET",
    data: opts.data,
    headers: { "X-Admin-Password": pw },
  });
  return r.data;
}

// -------------------- WIZARD MODAL --------------------
function GenerateWizard({ pw, config, onGenerated, onClose }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    goal: "promote_sale",
    topic: "",
    audience: "all_subscribers",
    tone: config?.brand?.default_tone || "friendly",
    length: "standard",
    layout: "product_grid",
    custom_instructions: "",
    include_new: false,
    include_best_sellers: false,
    include_on_sale: true,
    category_slugs: [],
    product_ids: [],
    max_products: 6,
    promotion: { percent_discount: "", dollar_discount: "", code: "", start_date: "", end_date: "", min_purchase: "" },
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPromo = (k, v) => setForm((f) => ({ ...f, promotion: { ...f.promotion, [k]: v } }));
  const toggleCategory = (slug) => setForm((f) => ({
    ...f,
    category_slugs: f.category_slugs.includes(slug)
      ? f.category_slugs.filter((s) => s !== slug)
      : [...f.category_slugs, slug],
  }));

  const generate = async () => {
    setBusy(true);
    try {
      // Trim empty promotion fields so the AI doesn't invent details we didn't set.
      const promotion = Object.fromEntries(
        Object.entries(form.promotion).filter(([, v]) => v !== "" && v !== null)
      );
      const payload = { ...form, promotion };
      const draft = await api("/generate", { method: "POST", data: payload }, pw);
      toast.success("Newsletter drafted");
      onGenerated(draft);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center overflow-y-auto p-4">
      <div className="max-w-3xl w-full my-8 bg-black border border-[#333] rounded-lg" data-testid="ai-newsletter-wizard">
        <div className="flex items-center justify-between p-4 border-b border-[#222] sticky top-0 bg-black z-10">
          <h3 className="font-display text-xl uppercase flex items-center gap-2">
            <Wand2 size={18} className="text-[var(--tc-red)]" /> Generate Newsletter with AI
          </h3>
          <button onClick={onClose} data-testid="wizard-close" className="p-2 hover:bg-[#111]"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-5">
          <Field label="Goal">
            <select className="input-tc" data-testid="wizard-goal" value={form.goal} onChange={(e) => set("goal", e.target.value)}>
              {(config.goals || []).map((g) => <option key={g} value={g}>{LABEL(g)}</option>)}
            </select>
          </Field>

          <Field label="Newsletter Topic" hint="Free-form. e.g. 'Summer sale on ready-made anime tees'">
            <input className="input-tc" data-testid="wizard-topic" value={form.topic}
              onChange={(e) => set("topic", e.target.value)} placeholder="50% off ready-made anime tees" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Audience">
              <select className="input-tc" data-testid="wizard-audience" value={form.audience} onChange={(e) => set("audience", e.target.value)}>
                {(config.audiences || []).map((a) => <option key={a} value={a}>{LABEL(a)}</option>)}
              </select>
            </Field>
            <Field label="Tone">
              <select className="input-tc" data-testid="wizard-tone" value={form.tone} onChange={(e) => set("tone", e.target.value)}>
                {(config.tones || []).map((t) => <option key={t} value={t}>{LABEL(t)}</option>)}
              </select>
            </Field>
            <Field label="Length">
              <select className="input-tc" data-testid="wizard-length" value={form.length} onChange={(e) => set("length", e.target.value)}>
                {(config.lengths || []).map((l) => <option key={l} value={l}>{LABEL(l)}</option>)}
              </select>
            </Field>
            <Field label="Layout">
              <select className="input-tc" data-testid="wizard-layout" value={form.layout} onChange={(e) => set("layout", e.target.value)}>
                {(config.layouts || []).map((l) => <option key={l} value={l}>{LABEL(l)}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Product Selection">
            <div className="flex flex-wrap gap-2">
              <ChipToggle testid="chip-sale" on={form.include_on_sale} onClick={() => set("include_on_sale", !form.include_on_sale)}>On sale</ChipToggle>
              <ChipToggle testid="chip-new" on={form.include_new} onClick={() => set("include_new", !form.include_new)}>Newly added</ChipToggle>
              <ChipToggle testid="chip-best" on={form.include_best_sellers} onClick={() => set("include_best_sellers", !form.include_best_sellers)}>Best sellers</ChipToggle>
            </div>
            {(config.categories || []).length > 0 && (
              <div className="mt-3">
                <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest mb-2">Categories (real from DB)</div>
                <div className="flex flex-wrap gap-2">
                  {config.categories.map((c) => (
                    <ChipToggle key={c.slug} testid={`cat-${c.slug}`} on={form.category_slugs.includes(c.slug)} onClick={() => toggleCategory(c.slug)}>
                      {c.slug} <span className="text-zinc-500 ml-1">({c.count})</span>
                    </ChipToggle>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-2 flex items-center gap-2">
              <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">Max products</span>
              <input type="number" min={1} max={12} className="input-tc w-20" data-testid="wizard-max-products"
                value={form.max_products} onChange={(e) => set("max_products", Math.max(1, Math.min(12, Number(e.target.value) || 6)))} />
            </div>
          </Field>

          <Field label="Promotion Details (leave blank for none)">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {[
                ["percent_discount", "% off"], ["dollar_discount", "$ off"],
                ["code", "Promo code"], ["min_purchase", "Min purchase $"],
                ["start_date", "Start date (YYYY-MM-DD)"], ["end_date", "End date (YYYY-MM-DD)"],
              ].map(([k, label]) => (
                <input key={k} className="input-tc" data-testid={`promo-${k}`}
                  placeholder={label} value={form.promotion[k]} onChange={(e) => setPromo(k, e.target.value)} />
              ))}
            </div>
          </Field>

          <Field label="Custom Instructions (optional)" hint="Extra guidance for the AI writer.">
            <textarea rows={4} className="input-tc" data-testid="wizard-custom"
              placeholder="Anything specific? e.g. 'Emphasize local Mississauga pickup and mention no minimum orders.'"
              value={form.custom_instructions} onChange={(e) => set("custom_instructions", e.target.value)} />
          </Field>
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-[#222]">
          <button onClick={onClose} className="btn-tc-secondary" data-testid="wizard-cancel">Cancel</button>
          <button onClick={generate} disabled={busy}
            className="btn-tc-primary inline-flex items-center gap-2" data-testid="wizard-generate">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {busy ? "Generating…" : "Generate"}
          </button>
        </div>
      </div>
    </div>
  );
}

// -------------------- EDITOR / PREVIEW --------------------
function DraftEditor({ pw, draft: initial, config, onClose, onSaved }) {
  const [draft, setDraft] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [previewMode, setPreviewMode] = useState("desktop");
  const [scheduleAt, setScheduleAt] = useState("");
  const [segment, setSegment] = useState(initial.audience_key || "all_subscribers");
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    // Auto-poll analytics for any draft that has been Sent.
    if (draft.status !== "Sent") return;
    let mounted = true;
    const fetchOnce = async () => {
      try {
        const a = await api(`/${draft.id}/analytics`, {}, pw);
        if (mounted) setAnalytics(a);
      } catch (_e) { /* ignore */ }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, 30_000);   // refresh every 30s
    return () => { mounted = false; clearInterval(t); };
  }, [draft.id, draft.status, pw]);

  const patch = async (data) => {
    setBusy(true);
    try {
      const d = await api(`/${draft.id}`, { method: "PATCH", data }, pw);
      setDraft(d);
      onSaved?.(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const updateSection = (idx, field, value) => {
    const sections = [...(draft.content?.sections || [])];
    sections[idx] = { ...sections[idx], [field]: value };
    patch({ content: { ...draft.content, sections } });
  };

  const removeSection = (idx) => {
    if (!confirm("Remove this section?")) return;
    const sections = (draft.content?.sections || []).filter((_, i) => i !== idx);
    patch({ content: { ...draft.content, sections } });
  };

  const rewriteSection = async (idx, mode) => {
    setBusy(true);
    try {
      const d = await api(`/${draft.id}/rewrite-section`, { method: "POST", data: { section_index: idx, mode } }, pw);
      setDraft(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rewrite failed");
    } finally { setBusy(false); }
  };

  const regenerateAll = async () => {
    if (!confirm("Regenerate the whole newsletter? Your manual edits will be saved to history.")) return;
    setBusy(true);
    try {
      const d = await api(`/${draft.id}/regenerate`, { method: "POST" }, pw);
      setDraft(d);
      toast.success("Regenerated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regenerate failed");
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!testTo.trim()) return toast.error("Enter a test email");
    setBusy(true);
    try {
      await api(`/${draft.id}/test-send`, { method: "POST", data: { to_email: testTo.trim() } }, pw);
      toast.success(`Test sent to ${testTo}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      const d = await api(`/${draft.id}/approve`, { method: "POST" }, pw);
      setDraft(d);
      toast.success("Approved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally { setBusy(false); }
  };

  const scheduleSend = async () => {
    if (!scheduleAt) return toast.error("Pick a date/time");
    setBusy(true);
    try {
      const iso = new Date(scheduleAt).toISOString();
      const r = await api(`/${draft.id}/schedule`, { method: "POST", data: { scheduled_for: iso, segment_key: segment } }, pw);
      setDraft(r.draft);
      toast.success(`Scheduled to ${r.estimated_recipients} subscribers`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Schedule failed");
    } finally { setBusy(false); }
  };

  const sendNow = async () => {
    if (!confirm(`Send this to '${segment}' RIGHT NOW? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const r = await api(`/${draft.id}/send-now`, { method: "POST", data: { segment_key: segment } }, pw);
      toast.success(`Queued to ${r.recipients} subscribers`);
      onSaved?.({ ...draft, status: "Sent" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  const c = draft.content || {};
  const previewWidth = previewMode === "mobile" ? 375 : previewMode === "tablet" ? 640 : 720;
  const isApproved = draft.status === "Approved" || draft.status === "Scheduled";

  return (
    <div className="fixed inset-0 z-50 bg-black/90 overflow-y-auto" data-testid="ai-newsletter-editor">
      <div className="sticky top-0 bg-black z-10 border-b border-[#222] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-2 hover:bg-[#111]"><X size={18} /></button>
          <div>
            <div className="font-display text-lg uppercase truncate max-w-[400px]">{draft.selected_subject || "Untitled"}</div>
            <div className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase">
              {draft.status} · {new Date(draft.updated_at || draft.created_at).toLocaleString()}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={regenerateAll} disabled={busy} className="btn-tc-secondary inline-flex items-center gap-2 text-xs h-9" data-testid="editor-regenerate">
            <RefreshCw size={14} /> Regenerate
          </button>
          {!isApproved && (
            <button onClick={approve} disabled={busy} className="btn-tc-primary inline-flex items-center gap-2 text-xs h-9" data-testid="editor-approve">
              ✓ Approve
            </button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 p-4">
        {/* LEFT: content editor */}
        <div className="space-y-3">
          <div className="surface-card p-4">
            <div className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase mb-2">Subject line</div>
            <select className="input-tc" data-testid="editor-subject" value={draft.selected_subject || ""}
              onChange={(e) => patch({ selected_subject: e.target.value })}>
              {(c.subjects || []).map((s, i) => <option key={i} value={s}>{s}</option>)}
              <option value={draft.selected_subject}>{draft.selected_subject} (current)</option>
            </select>
            <div className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase mt-3 mb-2">Preheader</div>
            <select className="input-tc" data-testid="editor-preheader" value={draft.selected_preheader || ""}
              onChange={(e) => patch({ selected_preheader: e.target.value })}>
              {(c.preheaders || []).map((s, i) => <option key={i} value={s}>{s}</option>)}
              <option value={draft.selected_preheader}>{draft.selected_preheader} (current)</option>
            </select>
          </div>

          <EditableRow label="Headline" value={c.headline} onChange={(v) => patch({ content: { ...c, headline: v } })} testid="edit-headline" />
          <EditableRow label="Subheadline" value={c.subheadline} onChange={(v) => patch({ content: { ...c, subheadline: v } })} testid="edit-subheadline" />
          <EditableRow label="Intro" value={c.intro} onChange={(v) => patch({ content: { ...c, intro: v } })} multiline testid="edit-intro" />

          {(c.sections || []).map((s, idx) => (
            <SectionEditor key={idx} idx={idx} section={s} busy={busy}
              onUpdate={(field, val) => updateSection(idx, field, val)}
              onRewrite={(mode) => rewriteSection(idx, mode)}
              onRemove={() => removeSection(idx)} />
          ))}

          <EditableRow label="Closing" value={c.closing} onChange={(v) => patch({ content: { ...c, closing: v } })} multiline testid="edit-closing" />

          <div className="surface-card p-4 space-y-3">
            <div className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase">Send / schedule</div>
            <div className="flex flex-wrap items-center gap-2">
              <input type="email" placeholder="test@example.com" className="input-tc flex-1 min-w-[200px]" value={testTo}
                onChange={(e) => setTestTo(e.target.value)} data-testid="test-email-input" />
              <button onClick={sendTest} disabled={busy || !testTo} className="btn-tc-secondary inline-flex items-center gap-2 text-xs h-10" data-testid="btn-test-send">
                <Send size={14} /> Send test
              </button>
            </div>

            {isApproved && (
              <div className="pt-3 border-t border-[#222] space-y-2">
                <select className="input-tc" data-testid="segment-select" value={segment} onChange={(e) => setSegment(e.target.value)}>
                  {(config.audiences || []).map((a) => <option key={a} value={a}>{LABEL(a)}</option>)}
                </select>
                <div className="flex flex-wrap gap-2">
                  <input type="datetime-local" className="input-tc flex-1 min-w-[240px]" value={scheduleAt}
                    onChange={(e) => setScheduleAt(e.target.value)} data-testid="schedule-datetime" />
                  <button onClick={scheduleSend} disabled={busy || !scheduleAt}
                    className="btn-tc-secondary inline-flex items-center gap-2 text-xs h-10" data-testid="btn-schedule">
                    <Clock size={14} /> Schedule
                  </button>
                  <button onClick={sendNow} disabled={busy}
                    className="btn-tc-primary inline-flex items-center gap-2 text-xs h-10" data-testid="btn-send-now">
                    <Play size={14} /> Send now
                  </button>
                </div>
              </div>
            )}
          </div>

          {analytics && (
            <div className="surface-card p-4" data-testid="analytics-card">
              <div className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase mb-3">Analytics</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <div className="font-display text-2xl" data-testid="stat-sent">{analytics.sent_count}</div>
                  <div className="font-mono text-[9px] text-zinc-500 tracking-widest uppercase">Sent</div>
                </div>
                <div>
                  <div className="font-display text-2xl" data-testid="stat-opens">{analytics.opens}</div>
                  <div className="font-mono text-[9px] text-zinc-500 tracking-widest uppercase">Unique opens ({analytics.open_rate}%)</div>
                </div>
                <div>
                  <div className="font-display text-2xl" data-testid="stat-clicks">{analytics.unique_clicks}</div>
                  <div className="font-mono text-[9px] text-zinc-500 tracking-widest uppercase">Unique clicks ({analytics.click_rate}%)</div>
                </div>
                <div>
                  <div className="font-display text-2xl" data-testid="stat-total-clicks">{analytics.total_clicks}</div>
                  <div className="font-mono text-[9px] text-zinc-500 tracking-widest uppercase">Total clicks</div>
                </div>
              </div>
              {analytics.top_url && (
                <div className="mt-3 pt-3 border-t border-[#222]">
                  <div className="font-mono text-[9px] text-zinc-500 tracking-widest uppercase mb-1">Most clicked link</div>
                  <a href={analytics.top_url} target="_blank" rel="noreferrer" className="font-mono text-xs text-[var(--tc-red)] underline break-all">
                    {analytics.top_url} <span className="text-zinc-500">({analytics.top_url_clicks})</span>
                  </a>
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT: live preview */}
        <div className="lg:sticky lg:top-16 self-start">
          <div className="flex gap-1 mb-2">
            {["desktop", "tablet", "mobile"].map((m) => (
              <button key={m} onClick={() => setPreviewMode(m)}
                className={`px-3 py-1 text-xs font-mono uppercase tracking-widest border ${previewMode === m ? "border-[var(--tc-red)] text-[var(--tc-red)]" : "border-[#333] text-zinc-500"}`}
                data-testid={`preview-mode-${m}`}>
                {m}
              </button>
            ))}
          </div>
          <div className="bg-white overflow-hidden" style={{ maxWidth: previewWidth, transition: "max-width 0.2s" }} data-testid="preview-frame">
            <iframe title="preview" style={{ width: "100%", height: 800, border: 0 }} srcDoc={draft.preview_html || ""} />
          </div>
        </div>
      </div>
    </div>
  );
}

// -------------------- Small pieces --------------------
function Field({ label, hint, children }) {
  return (
    <div>
      <label className="font-mono text-[10px] tracking-widest text-zinc-400 uppercase block mb-2">{label}</label>
      {children}
      {hint && <p className="font-mono text-[10px] text-zinc-600 mt-1">{hint}</p>}
    </div>
  );
}

function ChipToggle({ on, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-3 py-1.5 text-xs font-mono tracking-wide uppercase border transition ${on ? "border-[var(--tc-red)] bg-[var(--tc-red)]/10 text-white" : "border-[#333] text-zinc-400 hover:border-zinc-500"}`}>
      {children}
    </button>
  );
}

function EditableRow({ label, value, onChange, multiline, testid }) {
  const [v, setV] = useState(value || "");
  useEffect(() => setV(value || ""), [value]);
  const commit = () => { if (v !== value) onChange(v); };
  return (
    <div className="surface-card p-4">
      <div className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase mb-2">{label}</div>
      {multiline ? (
        <textarea rows={3} className="input-tc" value={v} data-testid={testid}
          onChange={(e) => setV(e.target.value)} onBlur={commit} />
      ) : (
        <input className="input-tc" value={v} data-testid={testid}
          onChange={(e) => setV(e.target.value)} onBlur={commit} />
      )}
    </div>
  );
}

function SectionEditor({ idx, section, busy, onUpdate, onRewrite, onRemove }) {
  return (
    <div className="surface-card p-4 space-y-2" data-testid={`section-${idx}`}>
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase">Section {idx + 1} — {section.kind || "text"}</div>
        <button onClick={onRemove} disabled={busy} className="text-xs text-zinc-500 hover:text-red-400" data-testid={`section-${idx}-remove`}>Remove</button>
      </div>
      <input className="input-tc" value={section.heading || ""} placeholder="Heading" data-testid={`section-${idx}-heading`}
        onChange={(e) => onUpdate("heading", e.target.value)} />
      <textarea rows={3} className="input-tc" value={section.body || ""} placeholder="Body" data-testid={`section-${idx}-body`}
        onChange={(e) => onUpdate("body", e.target.value)} />
      <div className="grid grid-cols-2 gap-2">
        <input className="input-tc" value={section.cta_text || ""} placeholder="Button text" data-testid={`section-${idx}-cta-text`}
          onChange={(e) => onUpdate("cta_text", e.target.value)} />
        <input className="input-tc" value={section.cta_url || ""} placeholder="Button URL" data-testid={`section-${idx}-cta-url`}
          onChange={(e) => onUpdate("cta_url", e.target.value)} />
      </div>
      <div className="flex flex-wrap gap-1 pt-2 border-t border-[#222]">
        {Object.entries(REWRITE_LABELS).map(([mode, label]) => (
          <button key={mode} onClick={() => onRewrite(mode)} disabled={busy}
            className="px-2 py-1 text-[10px] font-mono uppercase tracking-widest border border-[#333] text-zinc-400 hover:border-[var(--tc-red)] hover:text-[var(--tc-red)]"
            data-testid={`section-${idx}-ai-${mode}`}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

// -------------------- MAIN TAB --------------------
export default function AiNewsletterTab({ pw }) {
  const [config, setConfig] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [openDraft, setOpenDraft] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, list] = await Promise.all([
        api("/config", {}, pw),
        api("", {}, pw),
      ]);
      setConfig(cfg);
      setDrafts(list.drafts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load AI newsletter config");
    } finally {
      setLoading(false);
    }
  }, [pw]);

  useEffect(() => { refresh(); }, [refresh]);

  const openExisting = async (id) => {
    try {
      const d = await api(`/${id}`, {}, pw);
      setOpenDraft(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to open draft");
    }
  };

  const deleteDraft = async (id) => {
    if (!confirm("Delete this draft permanently?")) return;
    try {
      await api(`/${id}`, { method: "DELETE" }, pw);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const statusBadge = useMemo(() => ({
    "AI Draft": "text-purple-400",
    "Draft": "text-zinc-400",
    "Ready for Review": "text-amber-400",
    "Approved": "text-emerald-400",
    "Scheduled": "text-blue-400",
    "Sent": "text-zinc-600",
    "Cancelled": "text-red-500",
  }), []);

  return (
    <div data-testid="ai-newsletter-tab">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-3xl uppercase flex items-center gap-2">
            <Sparkles className="text-[var(--tc-red)]" size={24} /> AI Newsletter Generator
          </h2>
          <p className="font-mono text-xs text-zinc-500 mt-1">
            Generate → edit → preview → test → approve → schedule/send. Real products only, apparel-scoped, CASL-compliant.
          </p>
        </div>
        <button onClick={() => setShowWizard(true)} disabled={!config}
          className="btn-tc-primary inline-flex items-center gap-2 h-10" data-testid="btn-generate-newsletter">
          <Wand2 size={16} /> Generate Newsletter with AI
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-500 font-mono text-sm">Loading…</div>
      ) : drafts.length === 0 ? (
        <div className="surface-card p-12 text-center">
          <Sparkles size={32} className="mx-auto mb-3 text-zinc-700" />
          <div className="font-mono text-xs text-zinc-500 uppercase tracking-widest">
            No newsletters yet — click Generate Newsletter with AI to create your first.
          </div>
        </div>
      ) : (
        <div className="surface-card overflow-x-auto" data-testid="drafts-table">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-[#222]">
                <th className="font-mono text-[10px] tracking-widest text-zinc-500 p-4">SUBJECT</th>
                <th className="font-mono text-[10px] tracking-widest text-zinc-500 p-4">STATUS</th>
                <th className="font-mono text-[10px] tracking-widest text-zinc-500 p-4">AUDIENCE</th>
                <th className="font-mono text-[10px] tracking-widest text-zinc-500 p-4">UPDATED</th>
                <th className="font-mono text-[10px] tracking-widest text-zinc-500 p-4"></th>
              </tr>
            </thead>
            <tbody>
              {drafts.map((d) => (
                <tr key={d.id} className="border-b border-[#222] last:border-b-0 hover:bg-[#0a0a0a]" data-testid={`draft-row-${d.id}`}>
                  <td className="p-4 font-body text-sm max-w-[380px] truncate">{d.selected_subject || "Untitled"}</td>
                  <td className="p-4">
                    <span className={`font-mono text-[10px] tracking-widest uppercase ${statusBadge[d.status] || "text-zinc-400"}`}>{d.status}</span>
                  </td>
                  <td className="p-4 font-mono text-xs text-zinc-400">{LABEL(d.audience_key || "")}</td>
                  <td className="p-4 font-mono text-xs text-zinc-500">
                    {new Date(d.updated_at || d.created_at).toLocaleString()}
                  </td>
                  <td className="p-4 text-right">
                    <button onClick={() => openExisting(d.id)} className="text-[var(--tc-red)] font-mono text-[10px] tracking-widest uppercase mr-3" data-testid={`draft-open-${d.id}`}>
                      Open <ChevronRight size={12} className="inline" />
                    </button>
                    <button onClick={() => deleteDraft(d.id)} className="text-zinc-500 font-mono text-[10px] tracking-widest uppercase hover:text-red-400" data-testid={`draft-delete-${d.id}`}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showWizard && config && (
        <GenerateWizard pw={pw} config={config} onClose={() => setShowWizard(false)}
          onGenerated={(d) => { setShowWizard(false); setOpenDraft(d); refresh(); }} />
      )}
      {openDraft && config && (
        <DraftEditor pw={pw} draft={openDraft} config={config}
          onClose={() => { setOpenDraft(null); refresh(); }}
          onSaved={(d) => setOpenDraft(d)} />
      )}
    </div>
  );
}
